import { useState, useEffect } from 'react';
import { useApp } from '../App';
import api from '../api';
import BackButton from '../components/BackButton';
import { useI18n } from '../i18n/index.jsx';

const ACHIEVEMENTS = [
  { id: 'first_invoice', icon: '📄', name: 'Первая фактура', desc: 'Выстави первую фактуру', xp: 100 },
  { id: 'first_payment', icon: '💰', name: 'Первый платёж', desc: 'Получи первую оплату', xp: 150 },
  { id: 'verified', icon: '✅', name: 'Верификация', desc: 'Пройди верификацию профиля', xp: 200 },
  { id: 'first_referral', icon: '👥', name: 'Первый реферал', desc: 'Пригласи первого клиента', xp: 300 },
  { id: 'invoices_10', icon: '📊', name: '10 фактур', desc: 'Выстави 10 фактур', xp: 500 },
  { id: 'invoices_50', icon: '📈', name: '50 фактур', desc: 'Выстави 50 фактур', xp: 1000 },
  { id: 'turnover_10k', icon: '💎', name: '10К оборота', desc: 'Достигни 10 000 PLN оборота', xp: 500 },
  { id: 'turnover_100k', icon: '🏆', name: '100К оборота', desc: 'Достигни 100 000 PLN оборота', xp: 2000 },
  { id: 'referrals_5', icon: '🌟', name: '5 рефералов', desc: 'Пригласи 5 клиентов', xp: 1000 },
  { id: 'referrals_20', icon: '👑', name: '20 рефералов', desc: 'Пригласи 20 клиентов', xp: 3000 },
  { id: 'vat_refund', icon: '🧾', name: 'Возврат VAT', desc: 'Получи первый возврат VAT', xp: 250 },
  { id: 'crypto_user', icon: '₿', name: 'Криптан', desc: 'Пополни баланс в USDT', xp: 200 },
  { id: 'multi_currency', icon: '💱', name: 'Мультивалюта', desc: 'Используй 3+ валюты', xp: 300 },
  { id: 'support_helper', icon: '💬', name: 'Активист', desc: 'Напиши 10 сообщений в поддержку', xp: 150 },
  { id: 'early_bird', icon: '🐤', name: 'Ранняя пташка', desc: 'Зарегистрируйся в первый месяц', xp: 500 },
];

const LEVELS = [
  { level: 1, name: 'Новичок', minXp: 0, icon: '🌱' },
  { level: 2, name: 'Ученик', minXp: 500, icon: '📚' },
  { level: 3, name: 'Практик', minXp: 1500, icon: '💼' },
  { level: 4, name: 'Специалист', minXp: 3500, icon: '⭐' },
  { level: 5, name: 'Эксперт', minXp: 7000, icon: '🌟' },
  { level: 6, name: 'Мастер', minXp: 12000, icon: '💎' },
  { level: 7, name: 'Гуру', minXp: 20000, icon: '👑' },
  { level: 8, name: 'Легенда', minXp: 35000, icon: '🏆' },
];

export default function Achievements() {
  const { user } = useApp();
  const { t } = useI18n();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  async function loadStats() {
    try {
      const { data } = await api.get('/gamification/my-stats');
      setStats(data);
    } catch (e) {
      // Demo stats
      setStats({
        xp: 1850,
        unlocked: ['first_invoice', 'first_payment', 'verified', 'first_referral'],
        invoices_count: 12,
        turnover: 15000,
        referrals_count: 3,
      });
    } finally {
      setLoading(false);
    }
  }

  function getLevel(xp) {
    for (let i = LEVELS.length - 1; i >= 0; i--) {
      if (xp >= LEVELS[i].minXp) return LEVELS[i];
    }
    return LEVELS[0];
  }

  function getNextLevel(xp) {
    for (let i = 0; i < LEVELS.length; i++) {
      if (xp < LEVELS[i].minXp) return LEVELS[i];
    }
    return null;
  }

  if (loading) return <div className="page"><BackButton /><div className="loader">{t('common.loading')}</div></div>;

  const currentLevel = getLevel(stats?.xp || 0);
  const nextLevel = getNextLevel(stats?.xp || 0);
  const progress = nextLevel 
    ? ((stats?.xp - currentLevel.minXp) / (nextLevel.minXp - currentLevel.minXp)) * 100
    : 100;

  return (
    <div className="page">
      <BackButton />
      <h1>🏆 Достижения</h1>

      {/* Level Card */}
      <div style={{
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        borderRadius: 16,
        padding: 24,
        marginBottom: 24,
        textAlign: 'center',
      }}>
        <div style={{ fontSize: 64, marginBottom: 8 }}>{currentLevel.icon}</div>
        <div style={{ fontSize: 24, fontWeight: 'bold' }}>{currentLevel.name}</div>
        <div style={{ opacity: 0.8, marginBottom: 16 }}>Уровень {currentLevel.level}</div>
        
        <div style={{ fontSize: 32, fontWeight: 'bold', marginBottom: 8 }}>
          {stats?.xp || 0} XP
        </div>
        
        {nextLevel && (
          <>
            <div style={{
              height: 8,
              background: 'rgba(0,0,0,0.3)',
              borderRadius: 4,
              overflow: 'hidden',
              marginBottom: 8,
            }}>
              <div style={{
                width: `${progress}%`,
                height: '100%',
                background: 'linear-gradient(90deg, #10b981, #fbbf24)',
              }} />
            </div>
            <div style={{ fontSize: 12, opacity: 0.8 }}>
              До {nextLevel.name}: {nextLevel.minXp - (stats?.xp || 0)} XP
            </div>
          </>
        )}
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 24 }}>
        <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 12, padding: 12, textAlign: 'center' }}>
          <div style={{ fontSize: 24, fontWeight: 'bold' }}>{stats?.unlocked?.length || 0}</div>
          <div style={{ fontSize: 11, opacity: 0.7 }}>Достижений</div>
        </div>
        <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 12, padding: 12, textAlign: 'center' }}>
          <div style={{ fontSize: 24, fontWeight: 'bold' }}>{stats?.invoices_count || 0}</div>
          <div style={{ fontSize: 11, opacity: 0.7 }}>Фактур</div>
        </div>
        <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 12, padding: 12, textAlign: 'center' }}>
          <div style={{ fontSize: 24, fontWeight: 'bold' }}>{stats?.referrals_count || 0}</div>
          <div style={{ fontSize: 11, opacity: 0.7 }}>Рефералов</div>
        </div>
      </div>

      {/* Achievements List */}
      <h3>🎖 Все достижения</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
        {ACHIEVEMENTS.map(ach => {
          const unlocked = stats?.unlocked?.includes(ach.id);
          return (
            <div key={ach.id} style={{
              background: unlocked ? 'rgba(16,185,129,0.2)' : 'rgba(255,255,255,0.05)',
              borderRadius: 12,
              padding: 12,
              opacity: unlocked ? 1 : 0.6,
              border: unlocked ? '2px solid #10b981' : '2px solid transparent',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 24 }}>{ach.icon}</span>
                <span style={{ fontWeight: 'bold', fontSize: 13 }}>{ach.name}</span>
              </div>
              <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 4 }}>{ach.desc}</div>
              <div style={{ fontSize: 11, color: '#fbbf24' }}>+{ach.xp} XP</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
